// --------------------------------------------------------------
// Created On: 2022-01-14
// Author: Zachary Thomas
//
// Last Modified: 2025-11-04
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import { API, MAX_NAVBAR_ALERTS, MS_PER_SECOND } from "../../../constants/miscellaneous";
import useApi from "../../../hooks/useApi";
import apiRequest from "../../../utilities/api/apiRequest";
import { Link } from "react-router-dom";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { setAlerts, deleteAlert } from "src/redux/features/alertsSlice";
import useReduxSelector from "../../../hooks/useReduxSelector";
import styles from "./AlertIndicator.module.scss";
import useLastActive from "../../../hooks/useLastActive";
import { CHECK_ALERTS_MAX_INACTIVE_USER_SECONDS, CHECK_ALERTS_POLLING_DELAY_SECONDS } from "src/constants/polling";

// Periodically updating list of alerts.
export default function AlertIndicator(): Component {
  const [counter, setCounter] = useState<number>(0);
  const [dataPollCounter, setDataPollCounter] = useState<number>(0);
  const [unreadAlertCount, setUnreadAlertCount] = useState<number>(0);
  const [shake, setShake] = useState<boolean>(false);
  const alerts = useReduxSelector((state) => state.alerts);
  const dispatch = useReduxDispatch();
  const userInactiveSeconds = useLastActive(CHECK_ALERTS_POLLING_DELAY_SECONDS);

  // Get alert data from API.
  useApi(
    () => {
      return true;
    },
    {
      method: "GET",
      url: `${API}/alertmessage/all`,
      hideLog: true,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        dispatch(setAlerts(responseBody.alertMessages));
      } else {
        console.error("Internal server error. Unable to retrieve alert messages.");
      }
    },
    [dataPollCounter]
  );

  // When the number of unread alert messages change, update the counter.
  useEffect(() => {
    const newUnreadAlertCount = alerts.filter((alert) => alert.unread).length;
    if (newUnreadAlertCount > unreadAlertCount) {
      setUnreadAlertCount(newUnreadAlertCount);
      setShake(true);
    } else if (newUnreadAlertCount < unreadAlertCount) {
      setUnreadAlertCount(newUnreadAlertCount);
    } else if (newUnreadAlertCount === 0) {
      setShake(false);
    }
  }, [JSON.stringify(alerts), unreadAlertCount]);

  // Periodically poll alert data.
  useEffect(() => {
    const newTimerId = setTimeout(() => {
      setCounter((previousCounter) => previousCounter + 1);
      // Only poll data if the user is active.
      console.log(
        `Looking to see if alert data should be polled` +
          ` (inactive for ${userInactiveSeconds} seconds of ${CHECK_ALERTS_MAX_INACTIVE_USER_SECONDS} second limit).`
      );
      if (userInactiveSeconds < CHECK_ALERTS_MAX_INACTIVE_USER_SECONDS) {
        setDataPollCounter((prev) => prev + 1);
      } else {
        console.log("User is inactive. Alert data will not be polled.");
      }
    }, CHECK_ALERTS_POLLING_DELAY_SECONDS * MS_PER_SECOND);

    return () => {
      clearTimeout(newTimerId);
    };
  }, [counter]);

  // Delete an alert message.
  async function deleteAlertMessage(alertMessageId: number): Promise<void> {
    dispatch(deleteAlert(alertMessageId));
    const requestBody = { alertMessageIds: [alertMessageId] };
    void apiRequest(`${API}/alertmessage`, "DELETE", requestBody);
  }

  return (
    <div className={`${styles.container} dropdown mx-3`}>
      <button
        className={`${styles.btn} btn btn-primary ml-4 h-100`}
        type="button"
        onClick={() => setShake(false)}
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
        data-test="alert-indicator"
      >
        {unreadAlertCount > 0 && (
          <span className={styles.badge} data-test="alert-count">
            {unreadAlertCount < 100 ? unreadAlertCount : "99+"}
          </span>
        )}

        <i className={`${styles.bell} fa fa-fw fa-bell ${shake ? styles.shake : ""}`} />
      </button>

      <div className="dropdown-menu dropdown-menu-end" data-test="alerts-dropdown">
        {alerts
          .filter((alert) => alert.unread)
          .map((alert, i) => (
            <Fragment key={alert.alertMessageId}>
              {i < MAX_NAVBAR_ALERTS && (
                <div
                  className={`${styles.item} d-flex align-items-top dropdown-item pb-3`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div>
                    <i
                      className={
                        `${styles.icon} fa fa-fw me-2` +
                        ` fa-${alert.urgency.toLowerCase() === "info" ? "info-circle" : "exclamation-circle"}`
                      }
                      style={{
                        color: `var(--alert-${alert.urgency.toLowerCase()})`,
                      }}
                    />
                  </div>

                  <p
                    className={
                      `${styles.text} mb-0` +
                      ` ${alert.urgency.toLowerCase() === "read" ? styles.read : ""}` +
                      ` ${alert.urgency.toLowerCase() === "emergency" ? styles.emergency : ""}`
                    }
                  >
                    <span key={alert.alertMessageId}>{alert.message}</span>
                  </p>

                  <div className="mt-1 mx-4 d-inline-flex align-items-center">
                    <Link to={alert.alertUrl} className="router-link">
                      <i className="fa fa-fw fa-external-link" />
                    </Link>
                  </div>

                  <button
                    className={`${styles.dismissAlertButton} mt-1 mx-4 d-inline-flex align-items-center`}
                    onClick={() => deleteAlertMessage(alert.alertMessageId)}
                  >
                    <i className="fa fa-fw fa-times" data-test="dismiss-alert-button" />
                  </button>
                </div>
              )}
            </Fragment>
          ))}
        <Link to="/alert/overview" className="router-link">
          <button className={`${styles.viewAll} ${styles.item} dropdown-item`}>View All Alerts</button>
        </Link>
      </div>
    </div>
  );
}

interface AlertMessage {
  alertMessageId: number;
  alertCode: string;
  date: string;
  message: string;
  unread: boolean;
  urgency: string;
  alertUrl: string;
}

interface ResponseBody {
  alertMessages: AlertMessage[];
}
